/**
 * Currency Asset Mapping Utilities
 *
 * This module provides helper functions and mappings for resolving:
 *  - Currency flag/icon images
 *  - Display symbols (€, $, £, etc.)
 *  - Fallback behavior for unsupported currencies
 *
 * It centralizes all currency asset references in one place, ensuring consistent
 * rendering across table footers, currency pickers, and aggregated views.
 *
 * ----------------------------------------------------------------------------------
 * Mappings:
 * ----------------------------------------------------------------------------------
 *
 * `currencyIcons`:
 *    Maps ISO currency codes to imported image assets (PNG flags/icons).
 *
 * `currencySymbols`:
 *    Maps ISO codes to special symbol characters (€, $, £).
 *    If a currency is not listed here, its code is shown instead.
 *
 * ----------------------------------------------------------------------------------
 * Functions:
 * ----------------------------------------------------------------------------------
 *
 * `mapCCYAssets(currency: string)`
 *    Normalizes a currency code and returns:
 *      - `imgUrl`: The corresponding icon. Falls back to the ALL icon.
 *      - `symbol`: A special character (€, $, £) or the currency code itself.
 *      - `hasSpecialSymbol`: Boolean indicating whether a special symbol exists.
 *
 *    Used heavily in:
 *      - ExpandSum footer
 *      - Currency dropdown menu
 *      - Selected currency display components
 *
 *    Example:
 *        mapCCYAssets("usd")
 *        → { imgUrl: usd.png, symbol: "$", hasSpecialSymbol: true }
 *
 * ----------------------------------------------------------------------------------
 *
 * `getCurrencyIcon(currency: string | number)`
 *    Convenience helper to retrieve only the icon for a given currency.
 *    Always returns a valid image (falls back to ALL icon).
 *
 *    Example:
 *        getCurrencyIcon("GHS") → ghs.png
 *        getCurrencyIcon("unknown") → all.png
 *
 */

import eur from "../assets/img/png/ccy/eur.png";
import usd from "../assets/img/png/ccy/usd.png";
import gbp from "../assets/img/png/ccy/gbp.png";
import rsd from "../assets/img/png/ccy/rsd.png";
import all from "../assets/img/png/ccy/all.png";
import aoa from "../assets/img/png/ccy/aoa.png";
import nad from "../assets/img/png/ccy/nad.png";
import bwp from "../assets/img/png/ccy/bwp.png";
import ghs from "../assets/img/png/ccy/ghs.png";
import ugx from "../assets/img/png/ccy/ugx.png";
import bam from "../assets/img/png/ccy/bam.png";

const currencyIcons: Record<string, string> = {
  EUR: eur,
  USD: usd,
  GBP: gbp,
  RSD: rsd,
  AOA: aoa,
  NAD: nad,
  BWP: bwp,
  GHS: ghs,
  UGX: ugx,
  ALL: all,
  BAM: bam,
};

const currencySymbols: Record<string, string> = {
  EUR: "€",
  USD: "$",
  GBP: "£",
};

export const mapCCYAssets = (currency: string) => {
  const iso = currency.toUpperCase();

  const symbol = currencySymbols[iso] ?? iso;
  const hasSpecialSymbol = !!currencySymbols[iso];

  return {
    imgUrl: currencyIcons[iso] ?? all,
    symbol,
    hasSpecialSymbol,
  };
};

export const getCurrencyIcon = (currency: string | number): string => {
  const iso = String(currency).toUpperCase();
  return currencyIcons[iso] ?? all;
};
